import React from 'react';
import { MOCK_DATA } from './ProjectOverview';
interface Project {
  id: number;
  name: string;
  type: string;
  status: string;
  sellers: string;
  buyers: string;
  advisoryLead: string;
  sellerLegal: string;
  buyerLegal: string;
  lastUpdated?: string;
}
function getConsentCounts(projectName: string) {
  const data = MOCK_DATA[projectName] || MOCK_DATA.default;
  if (!data) return null;
  const rows = data.workstreams.rows;
  let ready = 0;
  let blocked = 0;
  let pending = 0;
  let notStarted = 0;
  for (const row of rows) {
    const allComplete = row.cells.every(c => c.status === 'complete');
    const anyAtRisk = row.cells.some(c => c.status === 'at_risk');
    const allNotStarted = row.cells.every(c => c.status === 'not_started');
    if (allComplete) ready++;else if (anyAtRisk) blocked++;else if (allNotStarted) notStarted++;else pending++;
  }
  return {
    ready,
    blocked,
    pending,
    notStarted,
    total: rows.length
  };
}
export function ProjectCard({
  project,
  onSelect
}: {
  project: Project;
  onSelect?: () => void;
}) {
  const isCompleted = project.status === 'Completed';
  const isActive = project.status === 'Active';
  const counts = isActive ? getConsentCounts(project.name) : null;
  const labelCls = `text-[11px] font-bold uppercase tracking-wider block mb-1 ${isCompleted ? 'text-gray-300' : 'text-gray-400'}`;
  const valueCls = `text-sm font-medium ${isCompleted ? 'text-gray-400' : 'text-gray-900'}`;
  const valueSubCls = `text-sm font-medium ${isCompleted ? 'text-gray-400' : 'text-gray-700'}`;
  return <div onClick={onSelect} className={`border rounded-lg overflow-hidden flex flex-col transition-all cursor-pointer ${isCompleted ? 'bg-gray-50/80 border-gray-200/60 opacity-70' : 'bg-white border-gray-200 shadow-sm hover:shadow-md'}`} data-id="element-448">
      {/* Header */}
      <div className="px-5 pt-5 pb-4 flex items-center justify-between" data-id="element-449">
        <div className="flex items-center gap-2.5" data-id="element-450">
          <h3 className={`text-lg font-semibold ${isCompleted ? 'text-gray-400' : 'text-gray-900'}`} data-id="element-451">
            {project.name}
          </h3>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${isActive ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-400 border-gray-200'}`} data-id="element-452">
            {project.status}
          </span>
        </div>
        <span className={`text-[11px] font-medium px-2 py-0.5 rounded border ${isCompleted ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-blue-50 text-blue-600 border-blue-100'}`} data-id="element-453">
          {project.type}
        </span>
      </div>

      {/* Metadata grid — mirrors the detail page metadata bar */}
      <div className="border-t border-gray-100 flex-1" data-id="element-454">
        <div className="grid grid-cols-2 border-b border-gray-100" data-id="element-455">
          <div className="px-5 py-3" data-id="element-456">
            <span className={labelCls} data-id="element-457">Seller</span>
            <span className={valueCls} data-id="element-458">{project.sellers}</span>
          </div>
          <div className="px-5 py-3 border-l border-gray-100" data-id="element-459">
            <span className={labelCls} data-id="element-460">Buyer</span>
            <span className={valueCls} data-id="element-461">{project.buyers}</span>
          </div>
        </div>
        <div className="grid grid-cols-3 border-b border-gray-100" data-id="element-462">
          <div className="px-5 py-3" data-id="element-463">
            <span className={labelCls} data-id="element-464">Advisory</span>
            <span className={valueSubCls} data-id="element-465">{project.advisoryLead}</span>
          </div>
          <div className="px-5 py-3 border-l border-gray-100" data-id="element-466">
            <span className={labelCls} data-id="element-467">Seller Legal</span>
            <span className={valueSubCls} data-id="element-468">{project.sellerLegal}</span>
          </div>
          <div className="px-5 py-3 border-l border-gray-100" data-id="element-469">
            <span className={labelCls} data-id="element-470">Buyer Legal</span>
            <span className={valueSubCls} data-id="element-471">{project.buyerLegal}</span>
          </div>
        </div>
      </div>

      {/* Consent tally — active only */}
      {isActive && counts && <div className="px-5 py-3 flex items-center gap-1.5 flex-wrap border-b border-gray-100" data-id="element-472">
          <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mr-1" data-id="element-473">
            Consents
          </span>
          {counts.ready > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200" data-id="element-474">
              <span className="w-1 h-1 rounded-full bg-emerald-400" data-id="element-475" />
              {counts.ready} ready
            </span>}
          {counts.blocked > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold text-rose-600 bg-rose-50 border border-rose-200" data-id="element-476">
              <span className="w-1 h-1 rounded-full bg-rose-400" data-id="element-477" />
              {counts.blocked} blocked
            </span>}
          {counts.pending > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold text-amber-600 bg-amber-50 border border-amber-200" data-id="element-478">
              <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-479" />
              {counts.pending} pending
            </span>}
          {counts.notStarted > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold text-gray-500 bg-gray-100 border border-gray-200" data-id="element-480">
              <span className="w-1 h-1 rounded-full bg-gray-400" data-id="element-481" />
              {counts.notStarted} not started
            </span>}
        </div>}

      {/* Footer */}
      <div className="px-5 py-3 mt-auto bg-gray-50/50" data-id="element-482">
        <span className={`text-xs font-medium transition-colors w-full text-center block ${isCompleted ? 'text-gray-400' : 'text-[#458b85] group-hover:text-[#3a7570]'}`} data-id="element-483">
          View Details →
        </span>
      </div>
    </div>;
}